// Dependencies
import { createFileRoute } from "@tanstack/react-router";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { ApplicationCard } from "@/components/ApplicationCard";
import { FeatureGrid } from "@/components/FeatureGrid";
import { CtaSection } from "@/components/CtaSection";
import { ImageTextSection } from "@/components/ImageTextSection";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

// Icons
import {
  Crosshair,
  RotateCcw,
  Orbit,
  Rocket,
  Gauge,
  Repeat,
  Recycle,
  Droplets,
  Snowflake,
  Blocks,
  Satellite,
  FlaskConical,
  Shield,
} from "lucide-react";

// Images
import heroImage from "@/assets/hero-arion.png";
import launchImage from "@/assets/launch.png";
import otvImage from "@/assets/otv.png";
import researchImage from "@/assets/ftl-cla.png";
import defenseImage from "@/assets/strategic.png";
import ctaImage from "@/assets/hero-contact.avif";
import arion2 from "@/assets/arion2.png";
import arion3 from "@/assets/arion3.png";
import finepLogo from "@/assets/partners/finep.png";

export const Route = createFileRoute("/$lang/products/arion-liquid-rocket-engine")({
  head: () => ({
    meta: [
      { title: i18n.t("products/arion-liquid-rocket-engine:metaTitle", "ARION Liquid Rocket Engine — BIZU Space") },
      {
        name: "description",
        content: i18n.t("products/arion-liquid-rocket-engine:metaDesc",
          "Detailed specifications of the ARION liquid rocket engine, a reusable propulsion unit using High-Test Peroxide (HTP) and Jet-A propellant.",
        ),
      },
      { property: "og:title", content: i18n.t("products/arion-liquid-rocket-engine:metaTitle", "ARION Liquid Rocket Engine — BIZU Space") },
      {
        property: "og:description",
        content: i18n.t("products/arion-liquid-rocket-engine:metaOgDesc",
          "A storable, regeneratively cooled 3-6kN liquid rocket engine for orbital injection and sounding rocket flights.",
        ),
      },
      { property: "og:url", content: "/products/arion-liquid-rocket-engine" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/products/arion-liquid-rocket-engine" }],
  }),
  component: ArionLiquidRocketEngine,
});

function ArionLiquidRocketEngine() {
  const { t } = useTranslation("products/arion-liquid-rocket-engine");

  const missionCards = [
    {
      title: t("mission.precision", "Precision orbital insertions"),
      icon: Crosshair,
    },
    {
      title: t("mission.multiburn", "Multi-burn missions"),
      icon: RotateCcw,
    },
    {
      title: t("mission.plane", "Plane changes and circularization"),
      icon: Orbit,
    },
    {
      title: t("mission.responsive", "Responsive launch campaigns"),
      icon: Rocket,
    },
  ];

  const capabilities = [
    {
      title: t("capabilities.thrustTitle", "Controllable Thrust"),
      description: t("capabilities.thrustDesc", "3-6kN across sea-level and vacuum conditions."),
      icon: Gauge,
    },
    {
      title: t("capabilities.ignitionTitle", "Multi-Ignition Capability"),
      description: t("capabilities.ignitionDesc", "Multiple restarts within a single mission."),
      icon: Repeat,
    },
    {
      title: t("capabilities.reusableTitle", "Reusable Architecture"),
      description: t("capabilities.reusableDesc", "Designed for operational cycles and extended burns."),
      icon: Recycle,
    },
    {
      title: t("capabilities.densityTitle", "High-Density Propellants"),
      description: t("capabilities.densityDesc", "Smaller tanks and optimized mass fraction."),
      icon: Droplets,
    },
    {
      title: t("capabilities.cryoTitle", "Non-Cryogenic System"),
      description: t("capabilities.cryoDesc", "Simplified logistics and faster mission readiness."),
      icon: Snowflake,
    },
    {
      title: t("capabilities.modularTitle", "Modular Integration"),
      description: t("capabilities.modularDesc", "Compatible with upper stages, OTVs, research platforms and airborne systems."),
      icon: Blocks,
    },
  ];

  const applications = [
    {
      title: t("applications.upperTitle", "Launch Vehicle Upper Stages"),
      description: t("applications.upperDesc", "Precision orbital injection and multi-burn mission operations and control."),
      icon: Rocket,
      image: launchImage,
      href: "/mlbr",
    },
    {
      title: t("applications.otvTitle", "Orbital Transfer Vehicles (OTVs)"),
      description: t("applications.otvDesc", "Re-orbiting, constellation phasing and servicing operations."),
      icon: Satellite,
      image: otvImage,
      href: "/products/atlas-htp",
    },
    {
      title: t("applications.researchTitle", "Research & Suborbital Platforms"),
      description: t("applications.researchDesc", "Technology demonstration, microgravity and educational missions."),
      icon: FlaskConical,
      image: researchImage,
      href: "/services/ftl",
    },
    /*
    {
      title: t("applications.strategicTitle", "Strategic & Defense Applications"),
      description: t("applications.strategicDesc",
        "Training rockets, UAV’s accelerator, aerial target rocket and tactical demonstrators.",
      ),
      icon: Shield,
      image: defenseImage,
      href: "/services/ftl",
    },
    */
  ];

  return (
    <PageShell>
      <PageHero
        title={t("heroTitle", "ARION Liquid Rocket Engine")}
        description={t("heroDesc",
          "A reusable liquid rocket engine designed for precision upper-stage propulsion and responsive aerospace operations. Using storable propellants (HTP / Jet-A-1), ARION delivers controllable thrust, multi-ignition capability and reliable performance for civil and strategic missions.",
        )}
        image={heroImage}
      >
        <div className="mt-8">
          <a href="https://www.finep.gov.br/" target="_blank" rel="noopener noreferrer" className="inline-block transition-transform hover:scale-105">
            <img src={finepLogo} alt="Finep Logo" className="h-12 w-auto object-contain mix-blend-screen opacity-90 hover:opacity-100" />
          </a>
        </div>
      </PageHero>

      {/* VIDEO YT / ORBITAL DELIVERY */}
      <section className="relative overflow-hidden bg-[#070707] py-28 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.08),transparent_45%)]" />

        <div className="container-bizu relative grid items-center gap-16 lg:grid-cols-[0.9fr_1.1fr]">
          <Reveal effect="left" className="space-y-8">
            <h2 className="display text-4xl uppercase leading-tight md:text-6xl">
              {t("precisionTitle", "Designed for Precise Orbital Delivery")}
            </h2>

            <div className="space-y-5 text-lg leading-relaxed text-white/70">
              <p>
                {t("precisionDesc1",
                  "Modern launch vehicles require controllable upper stages capable of delivering payloads to precise orbits.",
                )}
              </p>
              <p>
                {t("precisionDesc2",
                  "ARION was developed to meet this need — providing restart capability, controllable thrust between 3-6kN, and operational reliability across multiple mission profiles.",
                )}
              </p>
            </div>
          </Reveal>

          <Reveal effect="right">
            <div className="mx-auto flex justify-center">
              {/* Futuristic Telemetry HUD Frame */}
              <div className="relative p-3 bg-black/60 border border-white/10 rounded-3xl backdrop-blur-md shadow-[0_0_50px_rgba(45,48,214,0.15)] shrink-0">
                {/* Top Telemetry Info bar */}
                <div className="flex items-center justify-between px-2 pb-2 text-[10px] font-mono tracking-widest text-white/40 uppercase">
                  <span>feed // arion_lre_test</span>
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                    live link
                  </span>
                </div>

                {/* Video container */}
                <div className="relative h-[550px] w-[310px] overflow-hidden rounded-xl border border-white/10 bg-black">
                  <iframe
                    src="https://www.youtube.com/embed/k8UgULBldDI?autoplay=1&mute=1&controls=0&loop=1&playlist=k8UgULBldDI"
                    title="ARION Liquid Rocket Engine"
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* Bottom Telemetry Info bar */}
                <div className="flex items-center justify-between px-2 pt-2 text-[10px] font-mono tracking-widest text-white/40 uppercase">
                  <span>t+148s / active</span>
                  <span>thrust: 3-6kn</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="container-bizu mt-20">
          <FeatureGrid items={missionCards} columns={4} centered={true} />
        </div>
      </section>

      <ImageTextSection
        title={t("engineeredTitle", "Engineered for Real Operational Conditions")}
        image={arion2}
        variant="background"
        description={
          <>
            <p>
              {t("engineeredDesc1",
                "ARION was designed considering the whole aerospace environment — local suppliers, infrastructure and operational constraints.",
              )}
            </p>

            <p className="mt-5">
              {t("engineeredDesc2",
                "This approach ensures: High performance, reduced ground complexity, lower operational cost and faster deployment capability.",
              )}
            </p>

            <p className="mt-5">
              {t("engineeredDesc3",
                "Its architecture supports reuse, modular integration and scalable thrust variants for multiple aerospace platforms.",
              )}
            </p>
          </>
        }
      />
      <ImageTextSection
        title={t("storableTitle", "Storable Propellant Propulsion")}
        image={arion3}
        imagePosition="right"
        description={
          <>
            <p>
              {t("storableDesc1",
                "ARION engines use High-Test Peroxide (HTP) and Jet-A-1 — storable, safe-to-handle and globally accessible propellants.",
              )}
            </p>

            <p className="mt-5">
              {t("storableDesc2",
                "Unlike cryogenic systems, these propellants: Do not require extremely low temperatures, eliminate boil-off losses, reduce ground infrastructure complexity, enable long on-pad readiness and use of industrial components.",
              )}
            </p>

            <p className="mt-5">
              {t("storableDesc3",
                "HTP-based propulsion offers high density, simplified ignition, clean exhaust chemistry and multi-ignition capability — making ARION ideal for responsive operations and remote deployment scenarios. Also, this system has 2-modes of operation: Monopropellant and Bi-propellant – with different thrust ranges.",
              )}
            </p>
          </>
        }
      />

      {/* KEY CAPABILITIES */}
      <section className="relative overflow-hidden bg-black py-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 max-w-4xl">
            <span className="section-tag">{t("capabilitiesTitle", "Key Capabilities")}</span>

            <h2 className="display mt-5 text-4xl uppercase md:text-6xl">
              {t("capabilitiesSub", "Performance, reliability and mission flexibility")}
            </h2>
          </Reveal>
          <FeatureGrid items={capabilities} />
        </div>
      </section>

      {/* APPLICATIONS */}
      <section className="relative overflow-hidden bg-[#070707] py-28 text-white">
        <div className="container-bizu">
          <Reveal effect="up" className="mb-16 text-center">
            <h2 className="display mt-5 text-4xl uppercase md:text-6xl">{t("applicationsTitle", "Mission Applications")}</h2>
          </Reveal>

          <Reveal stagger className="grid gap-6 lg:grid-cols-3">
            {applications.map((item, idx) => (
              <ApplicationCard
                key={item.title}
                title={item.title}
                description={item.description}
                image={item.image}
                icon={item.icon}
                index={idx}
                href={item.href}
              />
            ))}
          </Reveal>
        </div>
      </section>

      <CtaSection
        image={ctaImage}
        title={t("ctaTitle", "Built for Precision. Designed for Autonomy.")}
        subtitle={t("ctaSubtitle",
          "ARION represents BIZU Space’s commitment to scalable, sovereign and high-performance liquid propulsion.",
        )}
        buttonText={t("ctaBtn", "Apply to use ARION")}
        href="/contact"
        secondaryButtonText={t("ctaSecondaryBtn", "Learn about HTP")}
        secondaryButtonHref="/products/htp"
      />
    </PageShell>
  );
}
